import { useRef, useState } from "react";
import axios from "axios";
import { API, authHeaders, imageSrc, formatApiError } from "../lib/api";
import { Pencil, Trash2, X, Upload } from "lucide-react";

const EMPTY_FORM = { title: "", price: "", sizes: "", image: "" };

export const ProductForm = ({ editing, onSaved, onCancel }) => {
  const [form, setForm] = useState(editing ? { ...editing } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { headers: authHeaders() });
      setForm((f) => ({ ...f, image: data.path }));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.image) {
      setError("Carica una foto del capo prima di salvare.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = { title: form.title, price: form.price, sizes: form.sizes, image: form.image };
      if (editing) {
        await axios.put(`${API}/admin/products/${editing.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: authHeaders() });
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-ink/15 bg-white px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-cognac";

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="product-form"
      className="bg-white border border-ink/10 rounded-2xl p-6 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-ink">
          {editing ? "Modifica capo" : "Nuovo capo"}
        </h2>
        {editing && (
          <button
            type="button"
            data-testid="product-form-cancel"
            onClick={onCancel}
            className="text-ink/50 hover:text-ink transition-colors"
            aria-label="Annulla modifica"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="flex items-start gap-4">
        <div className="w-24 h-32 rounded-xl overflow-hidden bg-cream border border-ink/10 shrink-0 flex items-center justify-center">
          {form.image ? (
            <img src={imageSrc(form.image)} alt="Anteprima capo" data-testid="product-image-preview" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[10px] text-ink/40 text-center px-1">Nessuna foto</span>
          )}
        </div>
        <div className="space-y-2">
          <button
            type="button"
            data-testid="product-upload-button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 bg-cream border border-ink/15 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider text-ink hover:border-cognac transition-colors disabled:opacity-50"
          >
            <Upload className="w-4 h-4" aria-hidden="true" />
            {uploading ? "Caricamento..." : "Carica foto"}
          </button>
          <p className="text-[11px] text-ink/50">JPG, PNG o WEBP — max 10 MB</p>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="product-file-input" />
        </div>
      </div>

      <input
        data-testid="product-title-input"
        placeholder="Nome del capo (es. Trench beige)"
        required
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        className={inputCls}
      />
      <input
        data-testid="product-price-input"
        placeholder="Prezzo (es. € 68,00)"
        required
        value={form.price}
        onChange={(e) => setForm({ ...form, price: e.target.value })}
        className={inputCls}
      />
      <input
        data-testid="product-sizes-input"
        placeholder="Taglie e dettagli (es. Taglie: S, M, L)"
        required
        value={form.sizes}
        onChange={(e) => setForm({ ...form, sizes: e.target.value })}
        className={inputCls}
      />

      {error && (
        <p data-testid="product-form-error" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <button
        type="submit"
        data-testid="product-form-submit"
        disabled={saving || uploading}
        className="w-full bg-cognac text-white rounded-full py-3 text-sm uppercase tracking-[0.14em] font-medium hover:bg-ink transition-colors disabled:opacity-50"
      >
        {saving ? "Salvataggio..." : editing ? "Salva modifiche" : "Aggiungi alla vetrina"}
      </button>
    </form>
  );
};

export const ProductRow = ({ product, onEdit, onDelete }) => {
  const [confirming, setConfirming] = useState(false);

  return (
    <div
      data-testid={`admin-product-${product.id}`}
      className="flex items-center gap-4 bg-white border border-ink/10 rounded-2xl p-4"
    >
      <div className="w-14 h-[72px] rounded-lg overflow-hidden bg-cream border border-ink/10 shrink-0">
        <img src={imageSrc(product.image)} alt={product.title} className="w-full h-full object-cover" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display font-semibold text-ink truncate">{product.title}</p>
        <p className="text-xs text-cognac font-medium">{product.price}</p>
        <p className="text-xs text-ink/60 truncate">{product.sizes}</p>
      </div>
      {confirming ? (
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid={`admin-delete-confirm-${product.id}`}
            onClick={() => onDelete(product.id)}
            className="text-xs font-semibold text-white bg-red-600 rounded-full px-3 py-1.5 hover:bg-red-700 transition-colors"
          >
            Conferma
          </button>
          <button
            data-testid={`admin-delete-cancel-${product.id}`}
            onClick={() => setConfirming(false)}
            className="text-xs font-semibold text-ink/60 rounded-full px-3 py-1.5 hover:text-ink transition-colors"
          >
            Annulla
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1 shrink-0">
          <button
            data-testid={`admin-edit-${product.id}`}
            onClick={() => onEdit(product)}
            className="p-2 text-ink/60 hover:text-cognac transition-colors"
            aria-label={`Modifica ${product.title}`}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            data-testid={`admin-delete-${product.id}`}
            onClick={() => setConfirming(true)}
            className="p-2 text-ink/60 hover:text-red-600 transition-colors"
            aria-label={`Elimina ${product.title}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
